import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, RefreshCw, Code2, Terminal } from "lucide-react";
import api from "@/lib/apiClient";
import { APP_ORIGIN } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function EmbedStudio({ business, onUpdated }) {
  const [copied, setCopied] = useState(false);
  const [platform, setPlatform] = useState("html");
  const token = business.token;
  const scriptUrl = `${APP_ORIGIN}/api/embed/${token}.js`;

  const snippets = {
    html: `<!-- Veloce AI Sales Assistant -->\n<script src="${scriptUrl}" async></script>`,
    wordpress: `// functions.php\nfunction veloce_widget() {\n  echo '<script src="${scriptUrl}" async></script>';\n}\nadd_action('wp_footer', 'veloce_widget');`,
    react: `// App.jsx\nimport { useEffect } from "react";\n\nuseEffect(() => {\n  const s = document.createElement("script");\n  s.src = "${scriptUrl}";\n  s.async = true;\n  document.body.appendChild(s);\n}, []);`,
  };

  const code = snippets[platform];

  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Script disalin ke clipboard");
    setTimeout(() => setCopied(false), 1800);
  };

  const regen = async () => {
    const { data } = await api.post(`/businesses/${business.id}/regenerate-token`);
    onUpdated({ ...business, token: data.token });
    toast.success("Token embed baru dibuat");
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display font-semibold flex items-center gap-2"><Code2 className="w-4 h-4" /> Script Embed Unik</h3>
            <Badge variant="outline" className="font-mono text-[11px]">token: {token.slice(0, 12)}…</Badge>
          </div>

          <Tabs value={platform} onValueChange={setPlatform}>
            <TabsList data-testid="select-embed-platform">
              <TabsTrigger value="html" data-testid="tab-embed-html">HTML / JS</TabsTrigger>
              <TabsTrigger value="wordpress" data-testid="tab-embed-wordpress">WordPress</TabsTrigger>
              <TabsTrigger value="react" data-testid="tab-embed-react">React</TabsTrigger>
            </TabsList>
            {["html", "wordpress", "react"].map((p) => (
              <TabsContent key={p} value={p}>
                <div className="relative">
                  <pre data-testid="code-embed-snippet"
                    className="bg-primary text-primary-foreground rounded-xl p-4 pr-14 text-xs overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap">
                    {snippets[p]}
                  </pre>
                  <Button data-testid="btn-copy-embed-script" size="icon" variant="secondary"
                    onClick={copy} className="absolute top-3 right-3 h-8 w-8">
                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          <div className="flex items-center gap-3 flex-wrap">
            <Button data-testid="btn-copy-embed-script-2" onClick={copy} className="bg-accent hover:bg-accent/90 text-white">
              <Copy className="w-4 h-4 mr-1.5" /> Salin Script
            </Button>
            <Button data-testid="btn-regenerate-token" variant="outline" onClick={regen}>
              <RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate Token
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Terminal className="w-4 h-4" /> Cara pasang</h4>
          <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
            <li>Salin script sesuai platform website kamu.</li>
            <li>Tempel sebelum tag <code className="font-mono text-xs bg-muted px-1 rounded">&lt;/body&gt;</code>.</li>
            <li>Widget chatbot otomatis muncul sesuai warna & pesan bisnis ini.</li>
          </ol>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Direct Widget Link</p>
        <a href={`${APP_ORIGIN}/widget/${token}`} target="_blank" rel="noreferrer"
          data-testid="link-widget-preview"
          className="block rounded-xl border border-border bg-card p-3 font-mono text-xs break-all hover:border-accent transition-colors">
          {APP_ORIGIN}/widget/{token}
        </a>
        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
          Buka link di atas untuk mencoba widget seperti yang dilihat pengunjung.
        </div>
      </div>
    </div>
  );
}
